<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  referral: { type: Object, required: true },
});

const { t } = useI18n();
const hasMediaError = ref(false);

const safeUrl = value => {
  if (!value) return '';

  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
};

const sourceUrl = computed(() => safeUrl(props.referral.sourceUrl));
const mediaUrl = computed(() =>
  safeUrl(
    props.referral.thumbnailUrl ||
      props.referral.imageUrl ||
      props.referral.mediaUrl ||
      props.referral.videoUrl
  )
);
const showMedia = computed(() => mediaUrl.value && !hasMediaError.value);
const isVideo = computed(
  () =>
    props.referral.mediaType === 'video' ||
    props.referral.mediaContentType?.startsWith('video/')
);

const handleMediaError = () => {
  hasMediaError.value = true;
};

watch(
  () => props.referral,
  () => {
    hasMediaError.value = false;
  }
);
</script>

<template>
  <aside
    class="w-full max-w-lg overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
    data-testid="whatsapp-referral"
  >
    <a
      v-if="sourceUrl"
      :href="sourceUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="block text-current no-underline hover:no-underline"
    >
      <div
        v-if="showMedia"
        class="relative aspect-video overflow-hidden bg-n-alpha-2"
      >
        <img
          :src="mediaUrl"
          :alt="referral.headline || t('CONVERSATION.WHATSAPP_REFERRAL.AD')"
          class="skip-context-menu size-full object-cover"
          @error="handleMediaError"
        />
        <span
          v-if="isVideo"
          class="pointer-events-none absolute inset-0 flex items-center justify-center bg-n-alpha-black2"
        >
          <span
            class="flex size-10 items-center justify-center rounded-full bg-n-solid-1 shadow-md"
          >
            <Icon icon="i-lucide-play" class="ms-0.5 size-4 text-n-slate-12" />
          </span>
        </span>
      </div>
      <div class="p-3">
        <div class="flex items-center gap-1 text-xs text-n-slate-10">
          <Icon icon="i-lucide-megaphone" class="size-3.5" />
          <span>{{ t('CONVERSATION.WHATSAPP_REFERRAL.AD') }}</span>
        </div>
        <p
          v-if="referral.headline"
          class="mb-0 mt-1 font-medium break-words text-n-slate-12"
        >
          {{ referral.headline }}
        </p>
        <p
          v-if="referral.body"
          class="mb-0 mt-1 line-clamp-3 whitespace-pre-line break-words text-n-slate-11"
        >
          {{ referral.body }}
        </p>
        <span
          class="mt-2 inline-flex items-center gap-1 text-xs font-medium text-n-brand"
        >
          {{ t('CONVERSATION.WHATSAPP_REFERRAL.OPEN') }}
          <Icon icon="i-lucide-external-link" class="size-3" />
        </span>
      </div>
    </a>
    <div v-else class="p-3">
      <div class="flex items-center gap-1 text-xs text-n-slate-10">
        <Icon icon="i-lucide-megaphone" class="size-3.5" />
        <span>{{ t('CONVERSATION.WHATSAPP_REFERRAL.AD') }}</span>
      </div>
      <p
        v-if="referral.headline"
        class="mb-0 mt-1 font-medium break-words text-n-slate-12"
      >
        {{ referral.headline }}
      </p>
      <p
        v-if="referral.body"
        class="mb-0 mt-1 line-clamp-3 whitespace-pre-line break-words text-n-slate-11"
      >
        {{ referral.body }}
      </p>
    </div>
  </aside>
</template>
